<script lang="ts">
	import { Heading } from "flowbite-svelte-v1";
	import DOMPurify from "isomorphic-dompurify";
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import P from "#lib/components/P.svelte";
	import TokenizedText from "#lib/components/TokenizedText.svelte";
	import { DEFAULT_HEADER_COMPONENT_TEXT, DEFAULT_PARAGRAPH_COMPONENT_TEXT, ELEMENT_DEFAULTS, TEXT_ELEMENTS, type TextElement } from "#lib/components/flow/editor/appearance/constants.js";
	import type { ComponentProps } from "#lib/components/page/ComponentProps.js";
	import { isTypeUriVersionAtLeast } from "#lib/components/page/utils.js";
	import { stringValue, structValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	let { id, style, page, mode, typeUri }: ComponentProps = $props();

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const config = $derived(structValue($page.config, id));
	const textColor = $derived(stringValue(config, "textColor"));
	const textElement = $derived(stringValue(config, "element") || TEXT_ELEMENTS[0].value) as TextElement;
	const textSize = $derived(stringValue(config, "textSize") || ELEMENT_DEFAULTS[textElement].textSize);
	const textAlignment = $derived(stringValue(config, "textAlignment") || ELEMENT_DEFAULTS[textElement].textAlignment);
	const fontWeight = $derived(stringValue(config, "fontWeight") || ELEMENT_DEFAULTS[textElement].fontWeight);
	const consumerClass = $derived(isTypeUriVersionAtLeast(typeUri, "2.0.0") ? "p-consumer-v2" : "p-consumer");

	function getThemeColorForElement(element: string) {
		if (element === "h1" || element === "h2") {
			return stringValue($page.themeOverrides, "headerTextColor");
		}
		return stringValue($page.themeOverrides, "paragraphTextColor");
	}

	const textAppearanceStyle = $derived(() => {
		const styleMap: Record<string, string | undefined> = {
			color: textColor || getThemeColorForElement(textElement),
			"text-align": textAlignment,
			"font-weight": fontWeight,
			"font-size": textSize ? `var(--text-${textSize})` : undefined,
		};

		return Object.entries(styleMap)
			.filter(([, v]) => v)
			.map(([k, v]) => `${k}: ${v};`)
			.join(" ");
	});

	const resolvedStyle = $derived(() => [style, textAppearanceStyle()].filter(Boolean).join("; "));

	const configText = $derived(stringValue(config, "text"));
	const isEditing = $derived(mode && mode === Mode.EDITING);

	const placeholder = $derived(textElement === "p" ? DEFAULT_PARAGRAPH_COMPONENT_TEXT : DEFAULT_HEADER_COMPONENT_TEXT);
	const resolvedFallback = $derived(isEditing ? placeholder : "");
	const showTokenizedPreview = $derived(isEditing && $tokenProvider?.hasSubstitutionToken(configText));
	const resolvedEditorDisplayText = $derived(configText !== undefined && configText !== "" ? configText : resolvedFallback);
</script>

{#snippet textContent()}
	{#if showTokenizedPreview}
		<TokenizedText provider={$tokenProvider} text={configText} {mode} />
	{:else if isEditing}
		{resolvedEditorDisplayText}
	{:else}
		<!-- eslint-disable-next-line svelte/no-at-html-tags -->
		{@html DOMPurify.sanitize(resolvedEditorDisplayText) ?? ""}
	{/if}
{/snippet}

{#if textElement === "h1" || textElement === "h2"}
	<Heading tag={textElement} class={twMerge(consumerClass, "wrap-break-word", stringValue(config, "class"))} style={resolvedStyle()}>
		{@render textContent()}
	</Heading>
{:else}
	<P class={twMerge(consumerClass, "wrap-break-word", stringValue(config, "class"))} style={resolvedStyle()}>
		{@render textContent()}
	</P>
{/if}
